import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { AgentDecision } from "../../api/types";
import { S } from "../../styles";
import { ConfidenceBadge } from "../../components/ConfidenceBadge";
import { CostCell } from "../../components/CostCell";
import { TokenCell } from "../../components/TokenCell";
import { LoadingState } from "../../components/QueryStates";

const AGENT_TYPE_LABELS: Record<string, string> = {
  sourcing: "Sourcing",
  quote_analysis: "Quote Analysis",
  recommendation: "Recommendation",
};

function agentTypeLabel(t: string): string {
  return AGENT_TYPE_LABELS[t] ?? t;
}

const AGENT_TYPE_COLOR: Record<string, string> = {
  sourcing: "#0891b2",
  quote_analysis: "#7c3aed",
  recommendation: "#4f46e5",
};

function agentTypeStyle(t: string): React.CSSProperties {
  const c = AGENT_TYPE_COLOR[t] ?? "#888";
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    background: c + "1a",
    color: c,
    border: `1px solid ${c}44`,
  };
}

function AgentDecisionRow({ d }: { d: AgentDecision }) {
  const [open, setOpen] = useState(false);
  const totalTokens = (d.prompt_tokens ?? 0) + (d.completion_tokens ?? 0);

  return (
    <>
      <tr
        style={{
          cursor: "pointer",
          background: open ? "#f8f9ff" : undefined,
          borderBottom: "1px solid #f0f0f0",
        }}
        onClick={() => setOpen((x) => !x)}
      >
        <td style={{ padding: "10px 12px", width: 24, color: "#aaa" }}>
          {open ? "▾" : "▸"}
        </td>
        <td style={{ padding: "10px 12px" }}>
          <span style={agentTypeStyle(d.agent_type)}>
            {agentTypeLabel(d.agent_type)}
          </span>
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontSize: 12,
            color: "#555",
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap" as const,
          }}
          title={d.model_used}
        >
          {d.model_used}
        </td>
        <td style={{ padding: "10px 12px" }}>
          {d.confidence !== null ? (
            <ConfidenceBadge score={d.confidence} />
          ) : (
            <span style={S.muted}>—</span>
          )}
        </td>
        <td style={{ padding: "10px 12px" }}>
          <TokenCell
            prompt={d.prompt_tokens}
            completion={d.completion_tokens}
          />
        </td>
        <td style={{ padding: "10px 12px" }}>
          {d.latency_ms !== null ? (
            <span style={{ fontSize: 13 }}>
              {d.latency_ms.toLocaleString()} ms
            </span>
          ) : (
            <span style={S.muted}>—</span>
          )}
        </td>
        <td style={{ padding: "10px 12px" }}>
          <CostCell cost={d.total_cost} />
        </td>
        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>
          {d.related_entity_type ? (
            <span>
              {d.related_entity_type}{" "}
              <span style={{ fontFamily: "monospace" }}>
                {d.related_entity_id?.slice(0, 8)}…
              </span>
            </span>
          ) : (
            <span style={S.muted}>—</span>
          )}
        </td>
        <td style={{ padding: "10px 12px", fontSize: 12, color: "#888" }}>
          {new Date(d.created_at).toLocaleString()}
        </td>
      </tr>
      {open && (
        <tr style={{ background: "#f8f9ff" }}>
          <td colSpan={9} style={{ padding: "0 24px 16px 48px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginTop: 12,
              }}
            >
              <div style={{ gridColumn: "1 / -1" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    color: "#888",
                    marginBottom: 4,
                    letterSpacing: 0.5,
                  }}
                >
                  Reasoning
                </div>
                {d.reasoning ? (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 13,
                      lineHeight: 1.5,
                      fontStyle: "italic",
                      color: "#444",
                      background: "#fff",
                      border: "1px solid #e8eaf0",
                      borderRadius: 6,
                      padding: "8px 12px",
                    }}
                  >
                    {d.reasoning}
                  </p>
                ) : (
                  <span style={S.muted}>No reasoning recorded.</span>
                )}
              </div>

              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    color: "#888",
                    marginBottom: 4,
                    letterSpacing: 0.5,
                  }}
                >
                  Input
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 11,
                    background: "#1a1a2e",
                    color: "#a8d8a8",
                    borderRadius: 6,
                    padding: "8px 12px",
                    overflow: "auto",
                    maxHeight: 200,
                  }}
                >
                  {JSON.stringify(d.input_data, null, 2)}
                </pre>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase" as const,
                    color: "#888",
                    marginBottom: 4,
                    letterSpacing: 0.5,
                  }}
                >
                  Output
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 11,
                    background: "#1a1a2e",
                    color: "#a8d8a8",
                    borderRadius: 6,
                    padding: "8px 12px",
                    overflow: "auto",
                    maxHeight: 200,
                  }}
                >
                  {JSON.stringify(d.output_data, null, 2)}
                </pre>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 20,
                marginTop: 10,
                fontSize: 12,
                color: "#888",
              }}
            >
              <span>
                ID:{" "}
                <span style={{ fontFamily: "monospace" }}>
                  {d.id.slice(0, 16)}…
                </span>
              </span>
              {d.created_by_user_id && (
                <span>
                  Created by:{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {d.created_by_user_id.slice(0, 8)}…
                  </span>
                </span>
              )}
              {totalTokens > 0 && (
                <span>
                  Total tokens: <strong>{totalTokens.toLocaleString()}</strong>{" "}
                  (
                  <span style={{ color: "#4f46e5" }}>
                    {(d.prompt_tokens ?? 0).toLocaleString()} prompt
                  </span>{" "}
                  +{" "}
                  <span style={{ color: "#7c3aed" }}>
                    {(d.completion_tokens ?? 0).toLocaleString()} completion
                  </span>
                  )
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function AgentDecisionsPanel() {
  const [filterType, setFilterType] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["agent-decisions", skip, filterType],
    queryFn: () => api.listAgentDecisions(skip, limit, filterType || undefined),
  });

  const decisions = data?.items ?? [];
  const total = data?.total ?? 0;

  const totalCost = decisions.reduce(
    (sum, d) => sum + (d.total_cost ? parseFloat(d.total_cost) : 0),
    0,
  );
  const totalTokens = decisions.reduce(
    (sum, d) => sum + (d.prompt_tokens ?? 0) + (d.completion_tokens ?? 0),
    0,
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap" as const,
          gap: 10,
        }}
      >
        <h2 style={{ ...S.h2, margin: 0 }}>
          🧠 Agent Decisions{" "}
          <span style={{ fontSize: 14, fontWeight: 400, color: "#888" }}>
            ({total} total)
          </span>
        </h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            style={{ ...S.select, width: "auto", marginBottom: 0 }}
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setSkip(0);
            }}
          >
            <option value="">All agent types</option>
            <option value="sourcing">Sourcing</option>
            <option value="quote_analysis">Quote Analysis</option>
            <option value="recommendation">Recommendation</option>
          </select>
          <button style={S.btnSm} onClick={() => void refetch()}>
            ↺ Refresh
          </button>
        </div>
      </div>

      {decisions.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            padding: "10px 16px",
            background: "#f8f9ff",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            flexWrap: "wrap" as const,
          }}
        >
          <span>
            <span style={{ color: "#888" }}>Page cost:</span>{" "}
            <strong>${totalCost.toFixed(5)}</strong>
          </span>
          <span>
            <span style={{ color: "#888" }}>Page tokens:</span>{" "}
            <strong>{totalTokens.toLocaleString()}</strong>
          </span>
          <span style={{ color: "#888", fontSize: 12 }}>
            (showing {decisions.length} of {total})
          </span>
        </div>
      )}

      {error && (
        <div style={S.card}>
          <p style={S.err}>⚠ {error.message}</p>
        </div>
      )}

      {isLoading && <LoadingState label="Loading agent decisions…" />}

      {!isLoading && decisions.length === 0 && !error && (
        <div style={S.card}>
          <p style={S.muted}>
            No agent decisions recorded yet. Submit a requirement to trigger the
            AI pipeline.
          </p>
        </div>
      )}

      {decisions.length > 0 && (
        <div
          style={{
            overflowX: "auto",
            border: "1px solid #e8eaf0",
            borderRadius: 10,
            background: "#fff",
          }}
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr
                style={{
                  background: "#f4f5f9",
                  borderBottom: "2px solid #e8eaf0",
                }}
              >
                <th style={{ width: 24, padding: "8px 12px" }} />
                {[
                  "Agent",
                  "Model",
                  "Confidence",
                  "Tokens (P+C)",
                  "Latency",
                  "Cost",
                  "Related Entity",
                  "Recorded",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "#555",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {decisions.map((d) => (
                <AgentDecisionRow key={d.id} d={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > limit && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 12,
            alignItems: "center",
          }}
        >
          <button
            style={S.btnGhost}
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - limit))}
          >
            ← Prev
          </button>
          <span style={S.muted}>
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            style={S.btnGhost}
            disabled={skip + limit >= total}
            onClick={() => setSkip(skip + limit)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
